/**
 * Phone country codes offered by every country-code picker in the app.
 *
 * The list holds every country that libphonenumber-js (the library behind
 * utils/phoneValidation.js) has numbering rules for, so each country in the picker also has a
 * dial code and a mobile digit count. Two exceptions: Ascension Island (+247) and Tristan da
 * Cunha, which have no flag on flagcdn.com.
 *
 * Each entry is { iso, code, name, aliases }:
 *  - iso:     lowercase ISO 3166-1 alpha-2 code. Builds the flag URL (https://flagcdn.com/24x18/iq.png)
 *             and the optional `countries.<iso>` translation key.
 *  - code:    dial code, e.g. "+964" (from the libphonenumber-js metadata).
 *  - name:    English name. Other languages use the browser's localized name (Intl.DisplayNames).
 *  - aliases: extra English search terms ("UAE", "USA", "Burma").
 *
 * Several countries share a dial code (+1 United States / Canada / Caribbean, +7 Russia /
 * Kazakhstan, +44 United Kingdom / Jersey…), so a picker stores the selected `iso`, never the code.
 *
 * The same file exists in tiryaq-doctor-pharmacy-app, tiryaq-lab-app, tiryaq-imaging-app,
 * tiryaq-nurse-app and tiryaq-physiotherapy-app (src/constants/countries.js). The admin panel has
 * a TypeScript copy (src/utils/countries.ts). Keep them in step.
 */
import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/core";
import metadata from "libphonenumber-js/metadata.mobile.json";

const NAMES = {
  ad: "Andorra",
  ae: "United Arab Emirates",
  af: "Afghanistan",
  ag: "Antigua and Barbuda",
  ai: "Anguilla",
  al: "Albania",
  am: "Armenia",
  ao: "Angola",
  ar: "Argentina",
  as: "American Samoa",
  at: "Austria",
  au: "Australia",
  aw: "Aruba",
  ax: "Åland Islands",
  az: "Azerbaijan",
  ba: "Bosnia and Herzegovina",
  bb: "Barbados",
  bd: "Bangladesh",
  be: "Belgium",
  bf: "Burkina Faso",
  bg: "Bulgaria",
  bh: "Bahrain",
  bi: "Burundi",
  bj: "Benin",
  bl: "Saint Barthélemy",
  bm: "Bermuda",
  bn: "Brunei",
  bo: "Bolivia",
  bq: "Caribbean Netherlands",
  br: "Brazil",
  bs: "Bahamas",
  bt: "Bhutan",
  bw: "Botswana",
  by: "Belarus",
  bz: "Belize",
  ca: "Canada",
  cc: "Cocos (Keeling) Islands",
  cd: "Democratic Republic of the Congo",
  cf: "Central African Republic",
  cg: "Republic of the Congo",
  ch: "Switzerland",
  ci: "Côte d'Ivoire",
  ck: "Cook Islands",
  cl: "Chile",
  cm: "Cameroon",
  cn: "China",
  co: "Colombia",
  cr: "Costa Rica",
  cu: "Cuba",
  cv: "Cape Verde",
  cw: "Curaçao",
  cx: "Christmas Island",
  cy: "Cyprus",
  cz: "Czech Republic",
  de: "Germany",
  dj: "Djibouti",
  dk: "Denmark",
  dm: "Dominica",
  do: "Dominican Republic",
  dz: "Algeria",
  ec: "Ecuador",
  ee: "Estonia",
  eg: "Egypt",
  eh: "Western Sahara",
  er: "Eritrea",
  es: "Spain",
  et: "Ethiopia",
  fi: "Finland",
  fj: "Fiji",
  fk: "Falkland Islands",
  fm: "Micronesia",
  fo: "Faroe Islands",
  fr: "France",
  ga: "Gabon",
  gb: "United Kingdom",
  gd: "Grenada",
  ge: "Georgia",
  gf: "French Guiana",
  gg: "Guernsey",
  gh: "Ghana",
  gi: "Gibraltar",
  gl: "Greenland",
  gm: "Gambia",
  gn: "Guinea",
  gp: "Guadeloupe",
  gq: "Equatorial Guinea",
  gr: "Greece",
  gt: "Guatemala",
  gu: "Guam",
  gw: "Guinea-Bissau",
  gy: "Guyana",
  hk: "Hong Kong",
  hn: "Honduras",
  hr: "Croatia",
  ht: "Haiti",
  hu: "Hungary",
  id: "Indonesia",
  ie: "Ireland",
  il: "Israel",
  im: "Isle of Man",
  in: "India",
  io: "British Indian Ocean Territory",
  iq: "Iraq",
  ir: "Iran",
  is: "Iceland",
  it: "Italy",
  je: "Jersey",
  jm: "Jamaica",
  jo: "Jordan",
  jp: "Japan",
  ke: "Kenya",
  kg: "Kyrgyzstan",
  kh: "Cambodia",
  ki: "Kiribati",
  km: "Comoros",
  kn: "Saint Kitts and Nevis",
  kp: "North Korea",
  kr: "South Korea",
  kw: "Kuwait",
  ky: "Cayman Islands",
  kz: "Kazakhstan",
  la: "Laos",
  lb: "Lebanon",
  lc: "Saint Lucia",
  li: "Liechtenstein",
  lk: "Sri Lanka",
  lr: "Liberia",
  ls: "Lesotho",
  lt: "Lithuania",
  lu: "Luxembourg",
  lv: "Latvia",
  ly: "Libya",
  ma: "Morocco",
  mc: "Monaco",
  md: "Moldova",
  me: "Montenegro",
  mf: "Saint Martin",
  mg: "Madagascar",
  mh: "Marshall Islands",
  mk: "North Macedonia",
  ml: "Mali",
  mm: "Myanmar",
  mn: "Mongolia",
  mo: "Macau",
  mp: "Northern Mariana Islands",
  mq: "Martinique",
  mr: "Mauritania",
  ms: "Montserrat",
  mt: "Malta",
  mu: "Mauritius",
  mv: "Maldives",
  mw: "Malawi",
  mx: "Mexico",
  my: "Malaysia",
  mz: "Mozambique",
  na: "Namibia",
  nc: "New Caledonia",
  ne: "Niger",
  nf: "Norfolk Island",
  ng: "Nigeria",
  ni: "Nicaragua",
  nl: "Netherlands",
  no: "Norway",
  np: "Nepal",
  nr: "Nauru",
  nu: "Niue",
  nz: "New Zealand",
  om: "Oman",
  pa: "Panama",
  pe: "Peru",
  pf: "French Polynesia",
  pg: "Papua New Guinea",
  ph: "Philippines",
  pk: "Pakistan",
  pl: "Poland",
  pm: "Saint Pierre and Miquelon",
  pr: "Puerto Rico",
  ps: "Palestine",
  pt: "Portugal",
  pw: "Palau",
  py: "Paraguay",
  qa: "Qatar",
  re: "Réunion",
  ro: "Romania",
  rs: "Serbia",
  ru: "Russia",
  rw: "Rwanda",
  sa: "Saudi Arabia",
  sb: "Solomon Islands",
  sc: "Seychelles",
  sd: "Sudan",
  se: "Sweden",
  sg: "Singapore",
  sh: "Saint Helena",
  si: "Slovenia",
  sj: "Svalbard and Jan Mayen",
  sk: "Slovakia",
  sl: "Sierra Leone",
  sm: "San Marino",
  sn: "Senegal",
  so: "Somalia",
  sr: "Suriname",
  ss: "South Sudan",
  st: "São Tomé and Príncipe",
  sv: "El Salvador",
  sx: "Sint Maarten",
  sy: "Syria",
  sz: "Eswatini",
  tc: "Turks and Caicos Islands",
  td: "Chad",
  tg: "Togo",
  th: "Thailand",
  tj: "Tajikistan",
  tk: "Tokelau",
  tl: "Timor-Leste",
  tm: "Turkmenistan",
  tn: "Tunisia",
  to: "Tonga",
  tr: "Turkey",
  tt: "Trinidad and Tobago",
  tv: "Tuvalu",
  tw: "Taiwan",
  tz: "Tanzania",
  ua: "Ukraine",
  ug: "Uganda",
  us: "United States",
  uy: "Uruguay",
  uz: "Uzbekistan",
  va: "Vatican City",
  vc: "Saint Vincent and the Grenadines",
  ve: "Venezuela",
  vg: "British Virgin Islands",
  vi: "U.S. Virgin Islands",
  vn: "Vietnam",
  vu: "Vanuatu",
  wf: "Wallis and Futuna",
  ws: "Samoa",
  xk: "Kosovo",
  ye: "Yemen",
  yt: "Mayotte",
  za: "South Africa",
  zm: "Zambia",
  zw: "Zimbabwe",
};

/** Other names people type for a country (English). */
const ALIASES = {
  ae: ["UAE", "Emirates", "Dubai"],
  ax: ["Aland"],
  bq: ["Bonaire", "Saba", "Sint Eustatius"],
  cd: ["DR Congo", "DRC", "Congo-Kinshasa"],
  cg: ["Congo", "Congo-Brazzaville"],
  ci: ["Ivory Coast"],
  cv: ["Cabo Verde"],
  cz: ["Czechia"],
  de: ["Deutschland"],
  gb: ["UK", "Great Britain", "Britain", "England", "Scotland", "Wales", "Northern Ireland"],
  kp: ["DPRK"],
  kr: ["Korea"],
  mk: ["Macedonia"],
  mm: ["Burma"],
  mo: ["Macao"],
  nl: ["Holland"],
  ps: ["Palestinian Territories"],
  sa: ["KSA"],
  sz: ["Swaziland"],
  tl: ["East Timor"],
  tr: ["Türkiye", "Turkiye"],
  us: ["USA", "US", "America"],
  va: ["Holy See"],
  vi: ["US Virgin Islands"],
};

export const COUNTRIES = Object.keys(NAMES)
  .map((iso) => ({
    iso,
    code: `+${getCountryCallingCode(iso.toUpperCase(), metadata)}`,
    name: NAMES[iso],
    aliases: ALIASES[iso] || [],
  }))
  .sort((a, b) => a.name.localeCompare(b.name, "en"));

const BY_ISO = new Map(COUNTRIES.map((country) => [country.iso, country]));

// Calling code digits ("1") -> the listed countries using it, main country first (metadata order).
const BY_CALLING_CODE = new Map();
Object.entries(metadata.country_calling_codes).forEach(([callingCode, isos]) => {
  const countries = isos
    .map((iso) => BY_ISO.get(iso.toLowerCase()))
    .filter(Boolean);
  if (countries.length > 0) BY_CALLING_CODE.set(callingCode, countries);
});

/** Iraq: the primary market, used when nothing else can be resolved. */
export const DEFAULT_COUNTRY_ISO = "iq";
export const DEFAULT_COUNTRY = BY_ISO.get(DEFAULT_COUNTRY_ISO);
export const DEFAULT_COUNTRY_CODE = DEFAULT_COUNTRY.code;

/** Flag image URL (24x18) for an ISO alpha-2 country code. */
export const flagUrl = (iso) =>
  `https://flagcdn.com/24x18/${String(iso || "").toLowerCase()}.png`;

/** Flag emoji for an ISO code (regional indicator letters), the fallback when the image fails. */
export const flagEmoji = (iso) =>
  String(iso || "")
    .toUpperCase()
    .replace(/[A-Z]/g, (letter) =>
      String.fromCodePoint(0x1f1a5 + letter.charCodeAt(0)),
    );

/** The listed country for an ISO code ("iq" or "IQ"), falling back to Iraq. */
export const findCountryByIso = (iso) =>
  BY_ISO.get(String(iso || "").toLowerCase()) || DEFAULT_COUNTRY;

const callingCodeDigits = (code) => String(code || "").replace(/\D/g, "");

/**
 * The country for a dial code ("+964"), falling back to Iraq. For a shared code ("+1") this is
 * `preferredIso` when it uses that code, otherwise the code's main country (United States).
 */
export const findCountryByCode = (code, preferredIso) => {
  const countries = BY_CALLING_CODE.get(callingCodeDigits(code));
  if (!countries) return DEFAULT_COUNTRY;
  const preferred = String(preferredIso || "").toLowerCase();
  return countries.find((country) => country.iso === preferred) || countries[0];
};

/** True when the dial code ("+964") belongs to a country in the list. */
export const isKnownCountryCode = (code) =>
  BY_CALLING_CODE.has(callingCodeDigits(code));

/** Arabic-Indic (٠-٩) and Persian (۰-۹) digits as ASCII digits. */
const toAsciiDigits = (value) =>
  String(value ?? "")
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

/**
 * Split a stored international number ("+9647701234567", "+964 7701234567") into its country
 * and national part ("7701234567": the digits after the dial code, without a trunk 0).
 *
 * The dial code is matched first (dial codes are prefix-free). For a shared code (+1, +7, +44…)
 * libphonenumber-js picks the exact country from the number itself (+1 416… is Canada); when it
 * cannot, `fallbackIso` wins if it uses that code, else the code's main country. A number that
 * matches no code keeps `fallbackIso` and all of its digits.
 *
 * @returns {{ country: { iso, code, name, aliases }, national: string }}
 */
export const splitMobileNumber = (fullNumber, fallbackIso = DEFAULT_COUNTRY_ISO) => {
  const fallback = findCountryByIso(fallbackIso);
  const digits = toAsciiDigits(fullNumber).replace(/\D/g, "");
  if (!digits) return { country: fallback, national: "" };

  for (let length = 1; length <= 3; length += 1) {
    const callingCode = digits.slice(0, length);
    const countries = BY_CALLING_CODE.get(callingCode);
    if (countries) {
      let country = findCountryByCode(callingCode, fallback.iso);
      if (countries.length > 1) {
        const parsed = parsePhoneNumberFromString(`+${digits}`, metadata);
        const detected = parsed?.country && BY_ISO.get(parsed.country.toLowerCase());
        if (detected && countries.includes(detected)) country = detected;
      }
      return { country, national: digits.slice(length).replace(/^0+/, "") };
    }
  }
  return { country: fallback, national: digits.replace(/^0+/, "") };
};

/** True when an international number ("+9647701234567") is a valid mobile number for its country. */
export const isValidMobileNumber = (fullNumber) => {
  const digits = toAsciiDigits(fullNumber).replace(/\D/g, "");
  if (!digits) return false;
  const parsed = parsePhoneNumberFromString(`+${digits}`, metadata);
  return Boolean(parsed && parsed.isValid());
};

/**
 * Same split with the dial code as a string, for screens that store it ("+964").
 * @returns {{ code: string, national: string, country: object }}
 */
export const splitDialCode = (fullNumber, fallbackCode = DEFAULT_COUNTRY_CODE) => {
  const { country, national } = splitMobileNumber(
    fullNumber,
    findCountryByCode(fallbackCode).iso,
  );
  return { code: country.code, national, country };
};

// Intl.DisplayNames per language; null when the browser has no names for it.
const regionNamesByLanguage = new Map();
const regionNames = (language) => {
  if (!regionNamesByLanguage.has(language)) {
    let names = null;
    try {
      names = new Intl.DisplayNames([language], { type: "region", fallback: "none" });
    } catch {
      names = null;
    }
    regionNamesByLanguage.set(language, names);
  }
  return regionNamesByLanguage.get(language);
};

/** Country name in the UI language ("العراق" for "ar"); the English name for English or as fallback. */
export const countryDisplayName = (country, language) => {
  if (!country) return "";
  const lang = String(language || "en").toLowerCase();
  if (lang.startsWith("en")) return country.name;
  let localized;
  try {
    localized = regionNames(lang)?.of(country.iso.toUpperCase());
  } catch {
    localized = undefined;
  }
  return localized || country.name;
};

/** Name shown in a picker: a `countries.<iso>` translation when the app has one, else the display name. */
export const countryLabel = (i18n, country) => {
  const key = `countries.${country.iso}`;
  return i18n && typeof i18n.exists === "function" && i18n.exists(key)
    ? i18n.t(key)
    : countryDisplayName(country, i18n?.language);
};

/** Lowercase search text without accents, Arabic diacritics or punctuation ("côte d'ivoire" → "cote divoire"). */
const normalizeSearchText = (value) =>
  toAsciiDigits(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u064b-\u065f\u0670\u0640]/g, "")
    .replace(/\u0671/g, "\u0627")
    .replace(/\u0649/g, "\u064a")
    .replace(/\u0629/g, "\u0647")
    .replace(/&/g, " and ")
    .replace(/['’`.,()]/g, "")
    .replace(/[-\s]+/g, " ")
    .trim();

const searchTerms = (country, label) => {
  const names = [label, country.name, ...country.aliases];
  // "St Lucia" finds "Saint Lucia".
  if (/\bSaint\b/.test(country.name)) names.push(country.name.replace(/\bSaint\b/g, "St"));
  return names.map(normalizeSearchText);
};

/**
 * Countries matching what the user typed: a country name in the UI language or English, an
 * alias, the ISO code, or the dial code ("964", "+96").
 *
 * Order: the exact ISO code ("us") or the main country of an exact dial code ("+1" → United
 * States) first, then names starting with the text, then names with a word starting with it,
 * then names containing it. `countries` order is kept within each group.
 *
 * @param {Array} countries  list in display order
 * @param {string} query
 * @param {(country) => string} getLabel  name shown for a country
 */
export const searchCountries = (countries, query, getLabel) => {
  const text = normalizeSearchText(query);
  if (!text) return countries;

  const dialDigits = text.replace(/^\+\s*/, "");
  const isDialCode = /^\d+$/.test(dialDigits);
  const matches = [];

  countries.forEach((country, index) => {
    let rank = -1;
    if (isDialCode) {
      const digits = country.code.slice(1);
      if (digits === dialDigits) {
        rank = BY_CALLING_CODE.get(digits)?.[0] === country ? 0 : 1;
      } else if (digits.startsWith(dialDigits)) {
        rank = 2;
      }
    } else if (text === country.iso) {
      rank = 0;
    } else {
      searchTerms(country, getLabel(country)).forEach((term) => {
        let termRank = -1;
        if (term.startsWith(text)) termRank = 1;
        else if (term.split(" ").some((word) => word.startsWith(text))) termRank = 2;
        else if (term.includes(text)) termRank = 3;
        if (termRank !== -1 && (rank === -1 || termRank < rank)) rank = termRank;
      });
    }
    if (rank !== -1) matches.push({ country, rank, index });
  });

  return matches
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((match) => match.country);
};
